import * as React from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
    faArrowRight,
    faChevronDown,
    faChevronUp,
    faMagnifyingGlass,
    faPlus,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Popover from '../../../core/components/Popover.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import { DEFAULT_LINE_ITEM_TYPE } from '../../constants.ts';
import { GROUP_SEPARATOR } from '../../pages/Assistance/useDocumentConfig.ts';
import { getFieldLabelKeys, useGetFieldLabel, useGetInputLabel } from '../../llmExtractorFields.ts';
import {
    FIELD_COLOR_CLASS,
    PromptEditor,
    PromptEditorHandle,
    PromptFieldMark,
    PromptFieldType,
    PromptMark,
    PromptMarkHit,
    PromptMenuItem,
} from '../PromptEditor.tsx';
import {
    CJK_PATTERN,
    getLabelPattern,
    getNamedIn,
    getNamesInText,
    isDistinctName,
    LlmExtractorInput,
    RuleFormValues,
} from './rules.ts';

const ArrowRightIcon = withIcon(faArrowRight);
const PlusIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faXmark);
const SearchIcon = withIcon(faMagnifyingGlass);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);

const TAG_CLASS = 'inline-flex items-center gap-1 rounded py-0.5 pl-1.5 pr-1 text-xs font-medium';
const FOCUS_CLASS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong';

interface AddOption {
    name: string;
    label: string;
    group: string;
}

interface RuleTagProps {
    label: string;
    type: PromptFieldType;
    isFaded: boolean;
    onInsert: () => void;
    onRemove: () => void;
}

// a picked name: a click inserts it into the instructions at the cursor, × removes it from the rule
const RuleTag = ({ label, type, isFaded, onInsert, onRemove }: RuleTagProps) => {
    const { t } = useTranslation('config');
    return (
        <span
            className={classnames(
                TAG_CLASS,
                FIELD_COLOR_CLASS[type],
                'cursor-pointer transition hover:opacity-100 hover:brightness-95',
                isFaded && 'opacity-55'
            )}
            onClick={onInsert}
        >
            <button
                type="button"
                title={
                    isFaded
                        ? t('configPage.fineTuning.box.insertUnnamedTitle')
                        : t('configPage.fineTuning.box.insertTitle')
                }
                className={classnames('cursor-pointer rounded border-0 bg-transparent p-0 text-current', FOCUS_CLASS)}
            >
                {label}
            </button>
            <button
                type="button"
                aria-label={t('configPage.fineTuning.box.removeTag', { name: label })}
                title={t('configPage.fineTuning.box.removeTag', { name: label })}
                onClick={(event) => {
                    event.stopPropagation();
                    onRemove();
                }}
                className={classnames(
                    'flex cursor-pointer items-center rounded border-0 bg-transparent p-0.5 text-current opacity-60 transition-opacity hover:opacity-100 focus-visible:opacity-100',
                    FOCUS_CLASS
                )}
            >
                <RemoveIcon className="text-[0.625rem]" />
            </button>
        </span>
    );
};

// subfields are left out: their labels ("Name", "City") would match almost any instructions
const useUnfilledFieldMarks = (outputs: string[], outputFields: string[]): PromptMark[] => {
    const { i18n } = useTranslation();
    const { language } = i18n;
    // built per list of outputs only: the form hands out a new outputFields array on every keystroke
    const allMarks = useMemo(
        () =>
            outputs.flatMap((name) => {
                const { field, keys } = getFieldLabelKeys(name);
                if (field.includes(GROUP_SEPARATOR)) return [];
                const labels = [...new Set(['en', language])].flatMap((lng) => {
                    const label = keys
                        .map((key) => i18n.getResource(lng, 'assistance', key))
                        .find((value): value is string => typeof value === 'string');
                    return label && label.length >= (CJK_PATTERN.test(label) ? 2 : 4) ? [label] : [];
                });
                return labels.length ? [{ key: name, pattern: getLabelPattern([...new Set(labels)]) }] : [];
            }),
        [outputs, i18n, language]
    );
    return useMemo(() => allMarks.filter(({ key }) => !outputFields.includes(key)), [allMarks, outputFields]);
};

interface SuggestionTagProps {
    label: string;
    title?: string;
    onClick: () => void;
}

export const SuggestionTag = ({ label, title = undefined, onClick }: SuggestionTagProps) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        className={classnames(
            TAG_CLASS,
            'cursor-pointer border border-dashed border-line-brand-default bg-transparent pr-1.5 text-content-brand-default transition-colors hover:bg-surface-strong',
            FOCUS_CLASS
        )}
    >
        <PlusIcon className="text-[0.625rem]" />
        {label}
    </button>
);

const optionMatches = (option: AddOption, search: string) =>
    `${option.label} ${option.name} ${option.group}`.toLowerCase().includes(search.trim().toLowerCase());

interface AddTagProps {
    type: PromptFieldType;
    options: AddOption[];
    onAdd: (name: string) => void;
}

// the dashed "+ Add data" / "+ Add field" tag: a search over what isn't picked yet, grouped
const AddTag = ({ type, options, onAdd }: AddTagProps) => {
    const { t } = useTranslation('config');
    const [isOpen, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const matches = options.filter((option) => optionMatches(option, search));
    const groups = [...new Set(matches.map((option) => option.group))];
    const pick = (name: string) => {
        onAdd(name);
        setSearch('');
        setOpen(false);
    };
    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                setOpen(open);
                setSearch('');
            }}
        >
            <Popover.Trigger asChild>
                <button
                    type="button"
                    className={classnames(
                        'inline-flex cursor-pointer items-center gap-1 rounded border border-dashed border-line-default bg-transparent px-1.5 py-0.5 text-xs font-medium text-content-medium transition-colors hover:bg-surface-strong hover:text-content-strong',
                        FOCUS_CLASS
                    )}
                >
                    <PlusIcon className="text-[0.625rem]" />
                    {type === 'input'
                        ? t('configPage.fineTuning.box.addData')
                        : t('configPage.fineTuning.box.addField')}
                </button>
            </Popover.Trigger>
            <Popover.Content align="start" className="w-80 p-0">
                <div className="border-0 border-b border-solid border-line-weak p-2">
                    <StringField
                        autoFocus
                        prefix={<SearchIcon />}
                        value={search}
                        onValueChange={(value) => setSearch(value ?? '')}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && matches[0]) {
                                event.preventDefault();
                                pick(matches[0].name);
                            }
                        }}
                        placeholder={t('configPage.fineTuning.box.search')}
                    />
                </div>
                <div className="flex max-h-72 flex-col overflow-y-auto py-1">
                    {!matches.length && (
                        <p className="px-3 py-2 text-sm text-content-medium">
                            {type === 'input'
                                ? t('configPage.fineTuning.box.noDataFound')
                                : t('configPage.fineTuning.box.noFieldsFound')}
                        </p>
                    )}
                    {groups.map((group) => (
                        <div key={group} className="flex flex-col">
                            <span className="px-3 pb-1 pt-2 text-xs font-medium text-content-medium">{group}</span>
                            {matches
                                .filter((option) => option.group === group)
                                .map((option) => (
                                    <button
                                        key={option.name}
                                        type="button"
                                        onClick={() => pick(option.name)}
                                        className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 py-1.5 text-left text-sm text-content-strong hover:bg-surface-medium focus-visible:bg-surface-medium focus-visible:outline-none"
                                    >
                                        <PromptFieldMark type={type} />
                                        <span className="flex min-w-0 flex-col">
                                            <span className="font-medium">{option.label}</span>
                                            <span className="text-xs text-content-weak">{option.name}</span>
                                        </span>
                                    </button>
                                ))}
                        </div>
                    ))}
                </div>
            </Popover.Content>
        </Popover>
    );
};

interface LegendTagProps {
    type: PromptFieldType;
    isFaded?: boolean;
}

const LegendTag = ({ type, isFaded = false }: LegendTagProps) => {
    const { t } = useTranslation('config');
    return (
        <span className={classnames(TAG_CLASS, 'pr-1.5', FIELD_COLOR_CLASS[type], isFaded && 'opacity-55')}>
            {type === 'input' ? t('configPage.fineTuning.legend.dataTag') : t('configPage.fineTuning.legend.fieldTag')}
        </span>
    );
};

const LEGEND_ROWS = [
    { type: 'input' as const, isFaded: false, text: 'configPage.fineTuning.legend.data' },
    { type: 'output' as const, isFaded: false, text: 'configPage.fineTuning.legend.field' },
    { type: 'output' as const, isFaded: true, text: 'configPage.fineTuning.legend.unnamedField' },
];

// the how-to under the box, and behind a toggle what each kind of tag means
const ColorLegend = () => {
    const { t } = useTranslation('config');
    const [isOpen, setOpen] = useState(false);
    return (
        <div className="flex flex-col gap-2 text-xs text-content-medium">
            {isOpen && (
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 rounded-lg bg-surface-medium px-3 py-2.5">
                    {LEGEND_ROWS.map(({ type, isFaded, text }) => (
                        <React.Fragment key={text}>
                            <span>
                                <LegendTag type={type} isFaded={isFaded} />
                            </span>
                            <span>{t(text)}</span>
                        </React.Fragment>
                    ))}
                </div>
            )}
            <div className="flex items-center justify-between gap-3">
                <span className="leading-6">
                    <Trans
                        t={t}
                        i18nKey="configPage.fineTuning.legend.howTo"
                        components={{
                            key: (
                                <kbd className="rounded border border-solid border-line-weak bg-surface-weak px-1 font-mono text-xs text-content-strong" />
                            ),
                        }}
                    />
                </span>
                <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(!isOpen)}
                    className={classnames(
                        'flex flex-none cursor-pointer items-center gap-2 rounded border-0 bg-transparent px-1.5 py-1 text-xs text-content-medium transition-colors hover:bg-surface-medium hover:text-content-strong',
                        isOpen && 'bg-surface-medium text-content-strong',
                        FOCUS_CLASS
                    )}
                >
                    <span className="flex items-center gap-0.5" aria-hidden>
                        {LEGEND_ROWS.map(({ type, isFaded }, index) => (
                            <span
                                key={index}
                                className={classnames(
                                    'size-2.5 rounded',
                                    FIELD_COLOR_CLASS[type],
                                    isFaded && 'opacity-55'
                                )}
                            />
                        ))}
                    </span>
                    {t('configPage.fineTuning.legend.toggle')}
                    {isOpen ? (
                        <ChevronUpIcon className="text-[0.625rem]" />
                    ) : (
                        <ChevronDownIcon className="text-[0.625rem]" />
                    )}
                </button>
            </div>
        </div>
    );
};

interface RuleFieldsBoxProps {
    form: UseFormReturn<RuleFormValues>;
    inputs: LlmExtractorInput[];
    outputs: string[];
    gqlDocumentType: string;
}

// The rule's instructions with its input data and output fields: a name inserted or typed into the instructions
// joins the lists, nothing leaves them unless its × is clicked
const RuleFieldsBox = ({ form, inputs, outputs, gqlDocumentType }: RuleFieldsBoxProps) => {
    const { t, i18n } = useTranslation('config');
    const getFieldLabel = useGetFieldLabel();
    const getInputLabel = useGetInputLabel();
    const [prompt, inputFields, outputFields]: [string, string[], string[]] = useWatch({
        control: form.control,
        name: ['prompt', 'inputFields', 'outputFields'],
    });
    const editorHandle = useRef<PromptEditorHandle>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const [isWrapped, setWrapped] = useState(false);
    // measured without the spacers, so leaving them out can't make the tags fit on one line again
    useLayoutEffect(() => {
        const bar = barRef.current;
        if (!bar) return;
        const measure = () => {
            const style = window.getComputedStyle(bar);
            const items = Array.from(bar.children).filter(
                (child): child is HTMLElement => child instanceof HTMLElement && !child.dataset.spacer
            );
            const itemsWidth = items.reduce((sum, item) => {
                const itemStyle = window.getComputedStyle(item);
                return (
                    sum +
                    item.getBoundingClientRect().width +
                    parseFloat(itemStyle.marginLeft) +
                    parseFloat(itemStyle.marginRight)
                );
            }, 0);
            const gapsWidth = (parseFloat(style.columnGap) || 0) * (items.length - 1);
            const lineWidth = bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
            setWrapped(itemsWidth + gapsWidth > lineWidth);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(bar);
        return () => observer.disconnect();
    }, [inputFields, outputFields]);

    // the rule's own names too, so a saved name outside today's options keeps its tag
    const outputNames = useMemo(() => [...new Set([...outputs, ...outputFields])], [outputs, outputFields]);
    const inputNames = useMemo(
        () => [...new Set([...inputs.map((input) => input.name), ...inputFields])],
        [inputs, inputFields]
    );
    const inputLabel = useCallback(
        (name: string) => getInputLabel(inputs.find((input) => input.name === name) ?? { name }),
        [inputs, getInputLabel]
    );
    const labelOf = (name: string, type: PromptFieldType) =>
        type === 'input' ? inputLabel(name) : getFieldLabel(name);

    // labelled once per change of the lists, not on every keystroke in the instructions
    const [inputOptions, outputOptions] = useMemo(() => {
        // the top-level header fields are grouped under the document type, e.g. "Order"
        const topLevelGroup = i18n.exists(`config:configPage.fineTuning.groups.topLevel.${gqlDocumentType}`)
            ? t(`configPage.fineTuning.groups.topLevel.${gqlDocumentType}`)
            : t('configPage.fineTuning.groups.headerFields');
        // in the add search the name goes under its field's group, so "City" under "Delivery address"
        const groupedField = (fieldName: string) => {
            const [parent, subfield] = fieldName.split(GROUP_SEPARATOR);
            if (parent === DEFAULT_LINE_ITEM_TYPE) {
                return { group: t('configPage.fineTuning.groups.lineItem'), label: getFieldLabel(fieldName) };
            }
            if (!subfield) return { group: topLevelGroup, label: getFieldLabel(fieldName) };
            const subfieldKey = `assistance:headerView.fieldNames.${fieldName}`;
            return {
                group: getFieldLabel(parent),
                label: i18n.exists(subfieldKey) ? t(subfieldKey) : getFieldLabel(fieldName),
            };
        };
        const sourceGroup = (name: string) =>
            name.startsWith('LineItem') || name === 'ArticleExtraData'
                ? t('configPage.fineTuning.groups.lineItem')
                : name.startsWith('Email')
                  ? t('configPage.fineTuning.groups.email')
                  : t('configPage.fineTuning.groups.document');
        return [
            inputs
                .filter((input) => !inputFields.includes(input.name))
                .map(
                    (input): AddOption =>
                        input.fieldPath
                            ? { name: input.name, ...groupedField(input.fieldPath.replaceAll('.', GROUP_SEPARATOR)) }
                            : { name: input.name, label: getInputLabel(input), group: sourceGroup(input.name) }
                ),
            outputs
                .filter((name) => !outputFields.includes(name))
                .map((name): AddOption => ({ name, ...groupedField(name) })),
        ];
    }, [inputs, outputs, inputFields, outputFields, gqlDocumentType, getFieldLabel, getInputLabel, t, i18n]);

    const changeFields = (updater: (prev: RuleFormValues) => RuleFormValues) => {
        const next = updater(form.getValues());
        form.setValue('prompt', next.prompt, { shouldDirty: true });
        form.setValue('inputFields', next.inputFields, { shouldDirty: true });
        form.setValue('outputFields', next.outputFields, { shouldDirty: true });
    };
    const changePrompt = (text: string) =>
        changeFields((prev) => {
            const names = [...inputNames, ...outputNames].filter(isDistinctName);
            const named = getNamedIn(text, names);
            const namedBefore = getNamedIn(prev.prompt, names);
            const isNew = (name: string) => named.has(name) && !namedBefore.has(name);
            return {
                ...prev,
                prompt: text,
                inputFields: [
                    ...prev.inputFields,
                    ...inputNames.filter((name) => !prev.inputFields.includes(name) && isNew(name)),
                ],
                outputFields: [
                    ...prev.outputFields,
                    ...outputNames.filter((name) => !prev.outputFields.includes(name) && isNew(name)),
                ],
            };
        });
    const addName = (name: string, type: PromptFieldType) =>
        changeFields((prev) => {
            const key = type === 'input' ? 'inputFields' : 'outputFields';
            return prev[key].includes(name) ? prev : { ...prev, [key]: [...prev[key], name] };
        });
    const removeName = (name: string, type: PromptFieldType) =>
        changeFields((prev) => {
            const key = type === 'input' ? 'inputFields' : 'outputFields';
            return { ...prev, [key]: prev[key].filter((item) => item !== name) };
        });

    const moreMenuItems = useMemo(
        (): PromptMenuItem[] => [
            ...outputNames
                .filter((name) => !outputFields.includes(name))
                .map((name) => ({
                    name,
                    type: 'output' as const,
                    label: getFieldLabel(name),
                    section: t('configPage.fineTuning.box.menuMoreFields'),
                })),
            ...inputNames
                .filter((name) => !inputFields.includes(name))
                .map((name) => ({
                    name,
                    type: 'input' as const,
                    label: inputLabel(name),
                    section: t('configPage.fineTuning.box.menuMoreData'),
                })),
        ],
        [outputNames, inputNames, outputFields, inputFields, getFieldLabel, inputLabel, t]
    );
    const listed = [
        ...outputFields.map((name) => ({ name, type: 'output' as const })),
        ...inputFields.map((name) => ({ name, type: 'input' as const })),
    ];
    const used = getNamesInText(prompt, [...outputNames, ...inputNames]);
    // "/" without typing: the rule's names not in the instructions yet, then everything else; typing also finds the
    // names already in the instructions
    const menuItems: PromptMenuItem[] = [
        ...listed
            .filter((item) => !used.has(item.name))
            .map((item) => ({
                ...item,
                label: labelOf(item.name, item.type),
                section: t('configPage.fineTuning.box.menuNotNamed'),
            })),
        ...moreMenuItems,
        ...listed
            .filter((item) => used.has(item.name))
            .map((item) => ({
                ...item,
                label: labelOf(item.name, item.type),
                section: t('configPage.fineTuning.box.menuNamed'),
            })),
    ];

    // extraction reads all listed data, named or not; a field the instructions don't name is still filled and
    // overwrites its value, except that a rule's only field is what its instructions are about anyway
    const isFaded = (name: string, type: PromptFieldType) =>
        type === 'output' && !used.has(name) && outputFields.length > 1;

    const marks = useUnfilledFieldMarks(outputs, outputFields);
    const namedFields = useMemo(
        () =>
            marks.flatMap(({ key, pattern }) => {
                const word = prompt.match(pattern)?.[0];
                return word ? [{ name: key, word }] : [];
            }),
        [marks, prompt]
    );
    const [offer, setOffer] = useState<PromptMarkHit | null>(null);
    const tags = (type: PromptFieldType) => (
        <>
            {(type === 'input' ? inputFields : outputFields).map((name) => (
                <RuleTag
                    key={name}
                    label={labelOf(name, type)}
                    type={type}
                    isFaded={isFaded(name, type)}
                    onInsert={() => editorHandle.current?.insert(name)}
                    onRemove={() => removeName(name, type)}
                />
            ))}
            {type === 'output' &&
                namedFields.map(({ name, word }) => (
                    <SuggestionTag
                        key={name}
                        label={getFieldLabel(name)}
                        title={t('configPage.fineTuning.box.namedFieldTitle', { word, field: getFieldLabel(name) })}
                        onClick={() => addName(name, 'output')}
                    />
                ))}
            <AddTag
                type={type}
                options={type === 'input' ? inputOptions : outputOptions}
                onAdd={(name) => addName(name, type)}
            />
        </>
    );

    return (
        <div className="flex flex-col gap-1.5">
            <Popover open={!!offer} onOpenChange={(open) => !open && setOffer(null)}>
                {offer && <Popover.Anchor virtualRef={{ current: offer.range }} />}
                <Popover.Content
                    align="start"
                    className="flex flex-col gap-3 text-sm"
                    // the caret stays in the instructions
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                >
                    {offer && (
                        <>
                            <p className="text-content-strong">
                                <Trans
                                    t={t}
                                    i18nKey="configPage.fineTuning.box.namedFieldOffer"
                                    values={{ word: offer.range.toString(), field: getFieldLabel(offer.key) }}
                                    components={{
                                        field: (
                                            <span
                                                className={classnames(TAG_CLASS, 'pr-1.5', FIELD_COLOR_CLASS.output)}
                                            />
                                        ),
                                    }}
                                />
                            </p>
                            <div className="flex justify-end gap-2">
                                <Button type="button" onClick={() => setOffer(null)}>
                                    {t('configPage.fineTuning.box.namedFieldNotNow')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={() => {
                                        addName(offer.key, 'output');
                                        setOffer(null);
                                    }}
                                >
                                    {t('configPage.fineTuning.box.addField')}
                                </Button>
                            </div>
                        </>
                    )}
                </Popover.Content>
            </Popover>
            {/* no overflow-hidden: it would clip the "/" menu, so the bar rounds its own corners */}
            <div
                className="flex flex-col rounded border border-solid border-line-default bg-surface-weak outline-2 outline-transparent transition-colors duration-200 hover:border-line-hover focus-within:border-line-focus focus-within:outline-line-weak"
                onKeyDown={() => setOffer(null)}
            >
                <PromptEditor
                    value={prompt}
                    onValueChange={changePrompt}
                    outputFields={outputFields}
                    inputFields={inputFields}
                    menuItems={menuItems}
                    onInsertField={addName}
                    marks={marks}
                    onMarkClick={setOffer}
                    ref={editorHandle}
                    placeholder={t('configPage.fineTuning.box.placeholder')}
                />
                <div
                    ref={barRef}
                    className="flex flex-wrap items-center gap-1 rounded-b border-0 border-t border-solid border-line-weak bg-surface-medium px-3 py-2"
                >
                    {tags('input')}
                    {/* while the tags fit on one line, the free room goes before the arrow and after the fields, so
                        the arrow sits near the middle */}
                    {!isWrapped && <span data-spacer aria-hidden className="flex-1" />}
                    <ArrowRightIcon aria-hidden className="mx-1 text-xs text-content-weak" />
                    {tags('output')}
                    {!isWrapped && <span data-spacer aria-hidden className="flex-1" />}
                </div>
            </div>
            <ColorLegend />
        </div>
    );
};

export default RuleFieldsBox;
